import { Injectable, InjectionToken, Injector } from '@angular/core';
import { EMPTY, Observable, Subject } from 'rxjs';
import { catchError, finalize, mergeMap, take, takeUntil } from 'rxjs/operators';
import { AutopropBackend } from './autoprop-backend';
import { TaskResponse } from './response.model';
import { Task } from './task.model';
import { WebSocketTaskResult } from './web-socket-message.model';
import { WebSocketService } from './web-socket.service';


export const TASK_INTERCEPTORS = new InjectionToken<TaskInterceptor>('TASK_INTERCEPTORS');

export interface TasksResult<T> {
    response: TaskResponse;
    tasks: Task<T>[];
}

export interface TaskInterceptor<T = unknown> {
    intercept(result: Observable<TasksResult<T>>): Observable<TasksResult<T>>;
}

export const sendResultToTask = (task: Task<any>, result: WebSocketTaskResult) => {
    if (!result.success) {
        task.error(result.payload);
        return;
    }
    task.next(result.payload);
};


@Injectable({
    providedIn: 'root',
})
export class TasksHandlerService {

    constructor(private injector: Injector) {}

    handle<T>(result: Observable<TasksResult<T>>) {
        const interceptors = this.injector.get<TaskInterceptor<T>[]>(TASK_INTERCEPTORS, []);
        return interceptors.reduce((result, interceptor) => interceptor.intercept(result), result);
    }
}


@Injectable({
    providedIn: 'root',
})
export class TasksService {

    pending: { [taskId: string]: Task<any> } = {};

    queue: Array<WebSocketTaskResult> = [];

    constructor(private backend: AutopropBackend, private wsService: WebSocketService, private handler: TasksHandlerService) {}

    cancelTasks(taskIds: string[])  {
        return this.backend.post('/api/task/terminate', { taskIds });
    }

    createTasks<T = any>(endpoint: string, body: any): Observable<{ response: TaskResponse, tasks: Task<T>[] }> {
        return this.wsService.connection.pipe(
            take(1),
            mergeMap(connection => {
                const params = { ...body, clientId: connection.clientId };
                return this.handler.handle(new Observable<{ response: TaskResponse, tasks: Task<T>[] }>(subscriber => {
                    const tasks = new Set<string>();
                    const aborted = new Subject<void>();
                    this.backend.post<TaskResponse>(endpoint, params, { ignoreLoadingBar: true })
                        .pipe(takeUntil(aborted))
                        .subscribe({
                            next: response => subscriber.next({
                                response,
                                tasks: response.payload.tasks.map(taskDef => {
                                    let args = {};
                                    if (body.taskRequests) {
                                        const taskRequest = body.taskRequests.find((t: any) => t.key === taskDef.key) || {};
                                        args = taskRequest.args || {};
                                    }
                                    const task = new Task<T>(
                                        taskDef.taskId,
                                        taskDef.resultId,
                                        taskDef.key,
                                        {
                                            args,
                                            partCount: taskDef.partCount,
                                            timeout: taskDef.timeout + 10,
                                        }
                                    );
                                    tasks.add(task.id);

                                    // register clean up subscriber
                                    task.result.pipe(finalize(() => {
                                        tasks.delete(task.id);
                                        delete this.pending[taskDef.taskId];
                                        if (tasks.size === 0) {
                                            subscriber.complete();
                                        }
                                    }), catchError(_ => EMPTY)).subscribe();

                                    this.pending[taskDef.taskId] = task;

                                    // check for queue if there is a result for this task
                                    for (const result of this.queue) {
                                        if (result.taskId === taskDef.taskId) {
                                            // use microtask queue to simulate async
                                            Promise.resolve().then(() => sendResultToTask(task, result));
                                        }
                                    }
                                    this.queue = this.queue.filter(m => m.taskId !== taskDef.taskId);
                                    return task;
                            })
                        }),
                        error: value => subscriber.error(value),
                    });

                    return () => {
                        aborted.next();
                        if (tasks.size > 0) {
                            this.cancelTasks([...tasks]).subscribe();
                        }
                    };
                }));
            })
        );
    }
}
